import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { BarChart2, Bell, CheckCircle2, ChevronDown, ChevronUp, ChevronsUpDown, ExternalLink, Heart, PlusCircle, SlidersHorizontal, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AppNav } from "@/components/AppNav";
import { ModelSearchBar } from "@/components/search/ModelSearchBar";
import { ListingDetailDrawer, type Listing } from "@/components/ListingDetailDrawer";
import { ComparadorPanel } from "@/components/ComparadorPanel";
import { DealBadge, dealTone, dealRank } from "@/components/DealBadge";
import { VisitedBadge } from "@/components/VisitedBadge";
import { pickTopDeals } from "@/lib/top-deals";
import { LoginGateModal } from "@/components/LoginGateModal";
import { useSession } from "@/hooks/useSession";
import { useProfile } from "@/hooks/useProfile";
import { supabase, goUrl } from "@/lib/supabase";
import { posthog } from "@/lib/posthog";
import { LOCATIONS } from "@/lib/locations";
import { fmtBR, fmtPrice, fmtCarTitle } from "@/lib/seo-format";
import {
  fetchListingsPage,
  SEARCH_PAGE_SIZE,
  type SearchDir,
  type SearchListing,
  type SearchParams,
  type SearchSort,
} from "@/lib/listings-search";
import { STAT_LISTINGS, STAT_SOURCES } from "@/lib/site-stats";

// URL-facing state — models stores comma-separated editorial slugs.
type SearchState = {
  models?: string;
  // Exact model_slug (one FIPE version), as opposed to `models` which matches
  // a whole family. Used by the /tabela cohort links; versao_label is display-only.
  versao?: string;
  versao_label?: string;
  min_year?: number;
  max_year?: number;
  min_km?: number;
  max_km?: number;
  min_price?: number;
  max_price?: number;
  location?: string;
  sort?: SearchSort;
  dir?: SearchDir;
  all?: string;
  price_dropped?: boolean;
};

const VALID_SORTS: SearchSort[] = ["price", "year", "km", "label", "deal_score"];

// Pins show at most 3 (one per cohort); 12 rows of deal_score-sorted results
// give enough cohort variety after dedup while keeping the query cheap.
const PINS_QUERY_SIZE = 12;

function parseNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function daysOnMarket(firstSeenAt: string | null | undefined): number | null {
  if (!firstSeenAt) return null;
  const d = Math.floor((Date.now() - new Date(firstSeenAt).getTime()) / 86_400_000);
  return d >= 1 ? d : null;
}

function expandSearch(s: SearchState): SearchParams {
  const params: SearchParams = {
    min_year: s.min_year,
    max_year: s.max_year,
    min_km: s.min_km,
    max_km: s.max_km,
    min_price: s.min_price,
    max_price: s.max_price,
    location: s.location,
    sort: s.sort,
    dir: s.dir,
  };
  const slugs = (s.models ?? "").split(",").filter(Boolean);
  // Pass slugs directly — the listings endpoint resolves via base_family exact match,
  // which correctly handles compound models (Corolla ≠ Corolla Cross) DB-side.
  if (slugs.length > 0) params.family = slugs.join(",");
  // buildSearchQuery gives `model` precedence over `family`.
  if (s.versao) params.model = s.versao;
  if (s.price_dropped) params.price_dropped = true;
  return params;
}

function slugToLabel(slug: string): string {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export const Route = createFileRoute("/buscar")({
  validateSearch: (raw: Record<string, unknown>): SearchState => {
    const s: SearchState = {};

    // New multi-model param
    if (typeof raw.models === "string" && raw.models) {
      s.models = raw.models;
    } else {
      if (typeof raw.model === "string" && raw.model) {
        s.models = raw.model;
      }
    }

    if (typeof raw.versao === "string" && raw.versao) {
      s.versao = raw.versao;
      if (typeof raw.versao_label === "string" && raw.versao_label) s.versao_label = raw.versao_label;
    }

    s.min_year = parseNumber(raw.min_year);
    s.max_year = parseNumber(raw.max_year);
    s.min_km = parseNumber(raw.min_km);
    s.max_km = parseNumber(raw.max_km);
    s.min_price = parseNumber(raw.min_price);
    s.max_price = parseNumber(raw.max_price);
    if (typeof raw.location === "string") s.location = raw.location;
    if (typeof raw.sort === "string" && VALID_SORTS.includes(raw.sort as SearchSort)) {
      s.sort = raw.sort as SearchSort;
    }
    if (raw.dir === "desc" || raw.dir === "asc") s.dir = raw.dir;
    if (raw.all === "1") s.all = "1";
    if (raw.price_dropped === "true" || raw.price_dropped === true) s.price_dropped = true;
    return s;
  },
  head: () => ({
    meta: [
      { title: "Buscar blindados — Busca Blindados" },
      {
        name: "description",
        content: `Vasculhe ${STAT_LISTINGS} blindados de ${STAT_SOURCES} sites num lugar só. Sem cadastro pra começar.`,
      },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: BuscarPage,
});

function BuscarPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const search = Route.useSearch();
  const accessToken = session?.access_token;
  const [modelLabels, setModelLabels] = useState<Record<string, string>>({});

  const updateSearch = (patch: Partial<SearchState>) => {
    const next: SearchState = { ...search, ...patch };
    (Object.keys(next) as (keyof SearchState)[]).forEach((k) => {
      if (next[k] === undefined || next[k] === "") delete next[k];
    });
    navigate({ to: "/buscar", search: next });
  };

  // Auto-set location from Cloudflare GeoIP cookie (cf_geo = "BR-SP") once on mount.
  const didAutoLocation = useRef(false);
  useEffect(() => {
    if (didAutoLocation.current || search.location) return;
    didAutoLocation.current = true;
    const match = document.cookie.match(/(?:^|;\s*)cf_geo=([^;]*)/);
    if (!match) return;
    const state = match[1].split("-")[1]?.toLowerCase();
    if (state && LOCATIONS.some((l) => l.slug === state)) {
      updateSearch({ location: state });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateAlert = (numericPatch?: Partial<SearchState>) => {
    const merged: SearchState = { ...search, ...(numericPatch ?? {}) };
    const alertSearch: Record<string, string> = { create: "1" };

    if (merged.versao) {
      alertSearch.model = merged.versao;
      alertSearch.model_label = merged.versao_label ?? slugToLabel(merged.versao);
    } else if (merged.models) {
      // Send the slug under `model` (alertas.tsx pre-fills the form from
      // searchParams.model). Writing `q` here used to be ignored —
      // searchParams.q isn't piped into the alert form anywhere.
      const firstSlug = merged.models.split(",")[0];
      alertSearch.model = firstSlug;
      alertSearch.model_label = modelLabels[firstSlug] ?? slugToLabel(firstSlug);
    }
    if (merged.min_year) alertSearch.min_year = String(merged.min_year);
    if (merged.max_year) alertSearch.max_year = String(merged.max_year);
    if (merged.min_km) alertSearch.min_km = String(merged.min_km);
    if (merged.max_km) alertSearch.max_km = String(merged.max_km);
    if (merged.min_price) alertSearch.min_price = String(merged.min_price);
    if (merged.max_price) alertSearch.max_price = String(merged.max_price);
    if (merged.location) alertSearch.location = merged.location;

    if (session) {
      navigate({ to: "/alertas", search: alertSearch });
    } else {
      const qs = new URLSearchParams(alertSearch).toString();
      navigate({ to: "/login", search: { next: `/alertas?${qs}`, context: "alert" } });
    }
  };

  return (
    <main className="min-h-screen text-foreground">
      <AppNav maxWidth="max-w-7xl" />
      <ResultsView
        search={search}
        session={session ?? null}
        accessToken={accessToken}
        updateSearch={updateSearch}
        onCreateAlert={handleCreateAlert}
        modelLabels={modelLabels}
        onAddLabel={(slug, label) => setModelLabels((prev) => ({ ...prev, [slug]: label }))}
      />
    </main>
  );
}

// ─── Results View ──────────────────────────────────────────────────────────

const COMPARADOR_MAX = 4;

function toDrawerListing(l: SearchListing): Listing {
  return {
    id: l.id,
    source: l.source ?? "",
    title: l.title,
    display_title: l.display_title,
    model_slug: l.model_slug,
    year: l.year,
    km: l.km,
    price: l.price,
  };
}

function ResultsView({
  search,
  session,
  accessToken,
  updateSearch,
  onCreateAlert,
  modelLabels,
  onAddLabel,
}: {
  search: SearchState;
  session: import("@/lib/supabase").Session | null;
  accessToken?: string;
  updateSearch: (patch: Partial<SearchState>) => void;
  onCreateAlert: (patch?: Partial<SearchState>) => void;
  modelLabels: Record<string, string>;
  onAddLabel: (slug: string, label: string) => void;
}) {
  const navigate = useNavigate();
  useProfile();
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [comparadorItems, setComparadorItems] = useState<Listing[]>([]);
  const [comparadorOpen, setComparadorOpen] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loginGateOpen, setLoginGateOpen] = useState(false);

  const params = useMemo(() => expandSearch(search), [search]);
  const hasModelFilter = (search.models ?? "").split(",").filter(Boolean).length > 0 || !!search.versao;
  const hasAnyFilter = hasModelFilter || !!search.price_dropped ||
    search.min_price != null || search.max_price != null ||
    search.min_year != null || search.max_year != null ||
    search.min_km != null || search.max_km != null ||
    !!search.location;

  const query = useInfiniteQuery({
    queryKey: ["search-listings", params, !!accessToken],
    queryFn: ({ pageParam = 1 }) =>
      fetchListingsPage(params, pageParam as number, accessToken),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    enabled: hasAnyFilter,
  });

  const total = query.data?.pages[0]?.total ?? 0;
  const statsReady = query.data != null;
  const minPriceVal = query.data?.pages[0]?.min_price;
  const maxPriceVal = query.data?.pages[0]?.max_price;
  const listings = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.data),
    [query.data],
  );

  // Pins ("que se destacam") come from a dedicated deal_score-sorted query, not
  // the list. Forcing sort here makes the query key value-stable across the
  // user's list sort, so the pins stay put regardless of how the list is ordered.
  const dealParams = useMemo<SearchParams>(
    () => ({ ...params, sort: "deal_score", dir: "desc" }),
    [params],
  );
  // Only the top few pins are shown, so fetch a small page — enough rows for
  // cohort variety after pickTopDeals dedups, but fast.
  const dealsQuery = useQuery({
    queryKey: ["search-top-deals", dealParams, !!accessToken],
    queryFn: () => fetchListingsPage(dealParams, 1, accessToken, PINS_QUERY_SIZE),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
    enabled: hasAnyFilter,
  });
  const topDeals = useMemo(
    () => pickTopDeals(dealsQuery.data?.data ?? []),
    [dealsQuery.data],
  );

  const isEmptyResult =
    hasAnyFilter && !query.isLoading && !dealsQuery.isLoading && !query.isError && listings.length === 0;
  const emptyStateProps = useMemo(() => {
    const activeFilters = (
      [
        ["model", hasModelFilter],
        ["price", search.min_price != null || search.max_price != null],
        ["year", search.min_year != null || search.max_year != null],
        ["km", search.min_km != null || search.max_km != null],
        ["location", !!search.location],
        ["price_dropped", !!search.price_dropped],
      ] as const
    ).filter(([, on]) => on).map(([name]) => name);
    return {
      has_model: hasModelFilter,
      model_slug: search.versao ?? (hasModelFilter ? (search.models ?? "").split(",")[0] : null),
      active_filters: activeFilters,
      is_logged_in: !!session,
    };
  }, [search, hasModelFilter, session]);
  const emptyStateKey = isEmptyResult ? JSON.stringify(params) : null;
  useEffect(() => {
    if (emptyStateKey) posthog.capture("search_empty_shown", emptyStateProps);
  }, [emptyStateKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session || listings.length === 0) { setFavoriteIds(new Set()); return; }
    supabase
      .from("favorites")
      .select("listing_id")
      .in("listing_id", listings.map((l) => l.id))
      .eq("user_id", session.user.id)
      .then(({ data }) => setFavoriteIds(new Set((data ?? []).map((r) => r.listing_id as string))));
  }, [session?.user.id, listings]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFavorite = async (e: React.MouseEvent, l: SearchListing) => {
    e.stopPropagation();
    if (!session) { setLoginGateOpen(true); return; }
    const isFav = favoriteIds.has(l.id);
    setFavoriteIds((prev) => { const n = new Set(prev); isFav ? n.delete(l.id) : n.add(l.id); return n; });
    if (isFav) {
      await supabase.from("favorites").delete().eq("listing_id", l.id).eq("user_id", session.user.id);
    } else {
      await supabase.from("favorites").insert({ user_id: session.user.id, listing_id: l.id, price_at_save: l.price });
      posthog.capture("listing_favorited", { listing_id: l.id });
    }
  };

  const toggleComparador = (listing: Listing) => {
    setComparadorItems((prev) => {
      if (prev.some((l) => l.id === listing.id)) return prev.filter((l) => l.id !== listing.id);
      if (prev.length >= COMPARADOR_MAX) return prev;
      return [...prev, listing];
    });
  };

  const currentSort: SearchSort = search.sort ?? "price";
  const currentDir: SearchDir = search.dir ?? "asc";
  const handleSortHeader = (col: SearchSort) => {
    updateSearch({
      sort: col,
      dir: currentSort === col
        ? (currentDir === "asc" ? "desc" : "asc")
        : (col === "price" ? "asc" : "desc"),
    });
  };

  const selectedModels = (search.models ?? "").split(",").filter(Boolean);
  const addModel = (slug: string, label?: string) => {
    if (selectedModels.includes(slug)) return;
    if (label) onAddLabel(slug, label);
    updateSearch({ models: [...selectedModels, slug].join(",") });
  };
  const removeModel = (slug: string) => {
    const next = selectedModels.filter((s) => s !== slug);
    // Keep results view when removing last chip — don't fall back to CatalogoHub
    updateSearch({ models: next.length ? next.join(",") : undefined, all: next.length ? undefined : "1" });
  };

  return (
    <>

      {/* Search bar + filter pills — same row on desktop, stacked on mobile */}
      <section className="mx-auto max-w-7xl px-6 pb-3">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
          {/* Fixed-width on desktop so the search box is just big enough for
              the placeholder + a little room; pills push to the right. */}
          <div className="md:w-72 md:shrink-0">
            <ModelSearchBar
              onPick={(patch) => { const slug = patch.family ?? patch.model; if (slug) addModel(slug, patch.label); }}
              onClear={() => {}}
              placeholder="Procure por modelos ou marcas"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden md:flex-none">
            {search.price_dropped && (
              <button
                type="button"
                onClick={() => updateSearch({ price_dropped: undefined })}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-green-500/40 bg-green-500/10 px-4 py-2.5 text-sm font-medium text-green-700 dark:text-green-400"
              >
                ↓ Queda de preço
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <LocationPill search={search} onUpdate={updateSearch} />
            <YearPill search={search} onUpdate={updateSearch} />
            <KmPill search={search} onUpdate={updateSearch} />
            <PricePill search={search} onUpdate={updateSearch} />
          </div>
        </div>
        {(selectedModels.length > 0 || search.versao) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {search.versao && (
              <button
                type="button"
                onClick={() => updateSearch({ versao: undefined, versao_label: undefined, all: "1" })}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs text-foreground"
              >
                {search.versao_label ?? slugToLabel(search.versao)}
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            )}
            {selectedModels.map((slug) => (
              <button
                key={slug}
                type="button"
                onClick={() => removeModel(slug)}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs text-foreground"
              >
                {modelLabels[slug] ?? slugToLabel(slug)}
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Two-column layout */}
      <section className="mx-auto max-w-7xl px-6 pb-6 pt-2">

        {/* Stats + create-alert bar — full-width row above the grid, only when any filter is active */}
        {hasAnyFilter && (
          <div className="surface-card mb-4 flex items-center justify-between gap-4 rounded-2xl px-5 py-3 text-sm">
            {statsReady ? (
              <p className="min-w-0 truncate text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{total.toLocaleString("pt-BR")}</span>
                {" "}{total === 1 ? "anúncio" : "anúncios"}
                {minPriceVal != null && (
                  <> · de <span className="font-medium text-foreground">{fmtPrice(minPriceVal)}</span></>
                )}
                {minPriceVal != null && maxPriceVal != null && (
                  <> até <span className="font-medium text-foreground">{fmtPrice(maxPriceVal)}</span></>
                )}
              </p>
            ) : (
              <div className="h-4 w-48 animate-pulse rounded-full dark:bg-white/10 bg-black/10" />
            )}
            {/* Desktop: inline button on the right. Mobile uses the FAB below instead. */}
            <button
              type="button"
              onClick={() => onCreateAlert()}
              className="hidden shrink-0 items-center gap-1.5 rounded-full bg-amber-500 px-3.5 py-1.5 text-xs font-medium text-white transition hover:bg-amber-400 md:inline-flex"
            >
              <Bell className="h-3.5 w-3.5" />
              Criar alerta com este filtro
            </button>
          </div>
        )}

        {/* Mobile-only FAB — same action, sticks to the bottom-right while scrolling.
            Hidden once the comparador pill appears so the two don't overlap. */}
        {hasAnyFilter && comparadorItems.length < 2 && (
          <button
            type="button"
            onClick={() => onCreateAlert()}
            className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition hover:opacity-90 md:hidden"
          >
            <Bell className="h-4 w-4" />
            Criar alerta
          </button>
        )}

        <div className="min-w-0">
            {/* Listings */}
            {!hasAnyFilter ? (
              <div className="surface-card rounded-2xl p-10 text-center">
                <p className="text-base font-semibold text-foreground">Faça sua busca</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Escolha um modelo ou use os filtros para ver os anúncios.
                </p>
              </div>
            ) : query.isLoading || dealsQuery.isLoading ? (
              <p className="surface-card rounded-2xl py-12 text-center text-sm text-muted-foreground">
                Carregando…
              </p>
            ) : query.isError && query.error?.message === "login_required" ? (
              <div className="surface-card rounded-2xl p-10 text-center">
                <p className="text-base font-semibold text-foreground">Faça login para ver mais</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Você viu os primeiros {SEARCH_PAGE_SIZE * 3} resultados. Crie uma conta gratuita para continuar.
                </p>
                <Link
                  to="/login"
                  search={{
                    next: typeof window !== "undefined" ? window.location.pathname + window.location.search : "/buscar",
                    context: "search",
                  }}
                  onClick={() => posthog.capture("search_login_wall_clicked")}
                  className="btn-primary mt-5 inline-flex rounded-full px-5 py-2.5 text-sm font-medium"
                >
                  Entrar ou criar conta
                </Link>
              </div>
            ) : query.isError ? (
              <p className="surface-card rounded-2xl py-12 text-center text-sm text-red-400">
                Não foi possível carregar os anúncios. Tente novamente em instantes.
              </p>
            ) : listings.length === 0 ? (
              <div className="surface-card rounded-2xl p-10 text-center">
                <p className="text-sm text-foreground">Nenhum anúncio encontrado com esses filtros.</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Crie um alerta e a gente te avisa quando aparecer um match.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    posthog.capture("search_empty_cta_clicked", emptyStateProps);
                    onCreateAlert();
                  }}
                  className="btn-primary mt-5 inline-flex rounded-full px-5 py-2.5 text-sm font-medium"
                >
                  Criar alerta com esses filtros
                </button>
              </div>
            ) : (
              <>
                {/* "Atualizando..." indicator — shown when refetching with stale data visible */}
                {query.isFetching && listings.length > 0 && (
                  <div className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                    Atualizando…
                  </div>
                )}

                {/* Hints banner for first-time visitors */}
                <NewUserHints />

                {/* Deal Score — "que se destacam" pins (Superfície 2) */}
                {topDeals.length > 0 && (
                  <section className="mb-4">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-base">🔥</span>
                      <h2 className="text-sm font-semibold text-foreground">
                        {topDeals.length === 1 ? "1 que se destaca" : `${topDeals.length} que se destacam`}
                      </h2>
                      <span className="text-xs text-muted-foreground/70">melhor preço pro KM que tem</span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {topDeals.map((l) => {
                        const dl = toDrawerListing(l);
                        return (
                          <div key={l.id}
                            onClick={() => setSelectedListing(dl)}
                            className="surface-card cursor-pointer rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.04] p-4 transition hover:border-emerald-500/50 dark:hover:bg-white/[0.04]"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="min-w-0 flex-1 font-medium text-foreground">
                                {fmtCarTitle(l.display_title ?? l.title)}
                                {l.location && <span className="ml-1.5 text-muted-foreground/70">({l.location.toUpperCase()})</span>}
                                <VisitedBadge id={l.id} className="ml-1.5 align-middle" />
                              </p>
                              <a href={goUrl(l.id, "web")}
                                target="_blank" rel="nofollow noopener noreferrer"
                                onClick={(e) => { e.stopPropagation(); posthog.capture("listing_external_clicked", { listing_id: l.id, context: "search_pin" }); }}
                                className="shrink-0 rounded-full p-1.5 text-muted-foreground/40 transition hover:text-muted-foreground">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                              {l.price != null && <span className="font-semibold text-primary">R$ {fmtBR(l.price)}</span>}
                              {l.km != null && <span className="text-muted-foreground">{fmtBR(l.km)} km</span>}
                              {l.year != null && <span className="text-muted-foreground">{l.year}</span>}
                            </div>
                            <div className="mt-2">
                              <DealBadge listing={l} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* Mobile: cards */}
                <div className="space-y-3 md:hidden">
                  <div className="flex justify-end">
                    <SortControl sort={currentSort} dir={currentDir} onChange={(sort, dir) => updateSearch({ sort, dir })} />
                  </div>
                  {listings.map((l) => {
                    const dl = toDrawerListing(l);
                    const isFav = favoriteIds.has(l.id);
                    const inComp = comparadorItems.some((c) => c.id === l.id);
                    return (
                      <div key={l.id}
                        onClick={() => setSelectedListing(dl)}
                        className="surface-card rounded-2xl p-4 transition dark:border-white/[0.12] border-black/[0.12] cursor-pointer dark:hover:bg-white/[0.09] hover:bg-black/[0.09] hover:border-primary/20 dark:hover:border-primary/30"
                      >
                        <div className="flex items-start gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground">
                              {fmtCarTitle(l.display_title ?? l.title)}
                              {l.location && <span className="ml-1.5 text-muted-foreground/70">({l.location.toUpperCase()})</span>}
                              <VisitedBadge id={l.id} className="ml-1.5 align-middle" />
                            </p>
                            {l.display_title && l.display_title !== l.title && (
                              <p className="mt-0.5 truncate text-xs text-muted-foreground/60">{l.title.length > 45 ? l.title.slice(0, 45) + "…" : l.title}</p>
                            )}
                            {(l.source || l.year) && (
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {[l.source, l.year].filter(Boolean).join(" · ")}
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-0.5">
                            <button type="button" onClick={(e) => toggleFavorite(e, l)}
                              title={isFav ? "Remover dos favoritos" : "Salvar"}
                              className="rounded-full p-1.5 transition">
                              <Heart className={`h-4 w-4 ${isFav ? "fill-primary text-primary" : "text-muted-foreground/40 hover:text-muted-foreground"}`} />
                            </button>
                            <a href={goUrl(l.id, "web")}
                              target="_blank" rel="nofollow noopener noreferrer"
                              onClick={(e) => { e.stopPropagation(); posthog.capture("listing_external_clicked", { listing_id: l.id, context: "search_list" }); }}
                              className="rounded-full p-1.5 text-muted-foreground/40 transition hover:text-muted-foreground">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                              {l.price != null && <span className="font-semibold text-primary">R$ {fmtBR(l.price)}</span>}
                              {l.km != null && <span className="text-muted-foreground">{fmtBR(l.km)} km</span>}
                            </div>
                            {(() => {
                              const days = daysOnMarket(l.first_seen_at);
                              const isNew = days != null && days <= 3;
                              return (
                                <>
                                  {/* Linha de badges de valor — altura reservada p/ alinhar cards mesmo sem badge */}
                                  <div className="mt-1 flex min-h-[22px] flex-wrap items-center gap-1.5">
                                    <DealBadge listing={l} />
                                    {l.price_dropped && l.price_drop_amount != null && (
                                      <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-[11px] font-medium text-green-600 ring-1 ring-green-400/30 dark:text-green-400">
                                        ↓ R$ {fmtBR(l.price_drop_amount)}
                                      </span>
                                    )}
                                  </div>
                                  {/* Linha de publicação + novo */}
                                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                    {isNew && <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-600 ring-1 ring-blue-400/30 dark:text-blue-400">Novo</span>}
                                    {days != null && (
                                      <span className="text-[11px] text-muted-foreground/60">
                                        Publicado há {days} {days === 1 ? "dia" : "dias"}
                                      </span>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                          <button type="button"
                            onClick={(e) => { e.stopPropagation(); toggleComparador(dl); }}
                            title={inComp ? "Remover do comparador" : "Adicionar ao comparador"}
                            className="rounded-full p-1 transition">
                            {inComp
                              ? <CheckCircle2 className="h-5 w-5 text-primary" />
                              : <PlusCircle className="h-5 w-5 text-muted-foreground/30 hover:text-muted-foreground" />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Desktop: table */}
                <div className="surface-card hidden overflow-hidden rounded-3xl md:block">
                  <table className="w-full table-fixed text-sm">
                    <thead>
                      <tr className="border-b dark:border-white/10 border-black/10 text-left text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                        <th className="px-5 py-3.5">Carro</th>
                        <th className="w-16 px-4 py-3.5 text-right">
                          <SortableHeader col="year" current={currentSort} dir={currentDir} onClick={handleSortHeader} className="justify-end">Ano</SortableHeader>
                        </th>
                        <th className="w-36 px-4 py-3.5 text-right">
                          <SortableHeader col="km" current={currentSort} dir={currentDir} onClick={handleSortHeader} className="justify-end">KM</SortableHeader>
                        </th>
                        <th className="w-32 px-4 py-3.5 text-right">
                          <SortableHeader col="price" current={currentSort} dir={currentDir} onClick={handleSortHeader} className="justify-end">Preço</SortableHeader>
                        </th>
                        <th className="w-24 px-3 py-3.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {listings.map((l) => {
                        const dl = toDrawerListing(l);
                        const isFav = favoriteIds.has(l.id);
                        const inComp = comparadorItems.some((c) => c.id === l.id);
                        return (
                          <tr key={l.id}
                            onClick={() => setSelectedListing(dl)}
                            className="border-b dark:border-white/5 border-black/5 transition cursor-pointer dark:hover:bg-white/[0.06] hover:bg-black/[0.06]"
                          >
                            <td className="px-5 py-3.5">
                              {(() => {
                                const days = daysOnMarket(l.first_seen_at);
                                const isNew = days != null && days <= 3;
                                const isPriceDrop = l.price_dropped && l.price_drop_amount != null;
                                const hasDeal = dealTone(l) != null || dealRank(l) != null;
                                const dateText = days != null ? `Publicado há ${days} ${days === 1 ? "dia" : "dias"}` : null;
                                const hasChips = hasDeal || isNew || isPriceDrop;
                                const rawSub = l.display_title && l.display_title !== l.title
                                  ? (l.title.length > 45 ? l.title.slice(0, 45) + "…" : l.title)
                                  : null;
                                return (
                                  <>
                                    {/* Row 1: display title + state abbreviation */}
                                    <p className="truncate font-medium text-foreground">
                                      {fmtCarTitle(l.display_title ?? l.title)}
                                      {l.location && <span className="ml-1.5 text-muted-foreground/70">({l.location.toUpperCase()})</span>}
                                      <VisitedBadge id={l.id} className="ml-1.5 align-middle" />
                                    </p>
                                    {/* Row 2: raw title (when different) · source */}
                                    {(rawSub || l.source) && (
                                      <p className="mt-0.5 flex items-baseline gap-2 truncate text-xs text-muted-foreground">
                                        {rawSub && <span className="min-w-0 truncate">{rawSub}</span>}
                                        {rawSub && l.source && <span className="text-muted-foreground/60">·</span>}
                                        {l.source && <span className="shrink-0">{l.source}</span>}
                                      </p>
                                    )}
                                    {/* Row 3: chips + "Publicado há X dias", all left-aligned */}
                                    {(hasChips || dateText) && (
                                      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                                        <DealBadge listing={l} size="xs" />
                                        {isNew && <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-600 ring-1 ring-blue-400/30 dark:text-blue-400">Novo</span>}
                                        {isPriceDrop && <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-[10px] font-medium text-green-600 ring-1 ring-green-400/30 dark:text-green-400">↓ R$ {fmtBR(l.price_drop_amount!)}</span>}
                                        {dateText && <span className="text-[11px] text-muted-foreground">{dateText}</span>}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-3.5 text-right tabular-nums text-muted-foreground">{l.year ?? "—"}</td>
                            <td className="px-4 py-3.5 text-right tabular-nums text-muted-foreground">{l.km != null ? `${fmtBR(l.km)} km` : "—"}</td>
                            <td className="px-4 py-3.5 text-right">
                              <span className="font-semibold tabular-nums text-primary">{l.price != null ? `R$ ${fmtBR(l.price)}` : "—"}</span>
                            </td>
                            <td className="px-3 py-3.5" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-0.5">
                                <button type="button"
                                  onClick={() => toggleComparador(dl)}
                                  title={inComp ? "Remover do comparador" : "Adicionar ao comparador"}
                                  className="rounded-full p-1 transition">
                                  {inComp
                                    ? <CheckCircle2 className="h-5 w-5 text-primary" />
                                    : <PlusCircle className="h-5 w-5 text-muted-foreground/30 hover:text-muted-foreground" />}
                                </button>
                                <button type="button" onClick={(e) => toggleFavorite(e, l)}
                                  title={isFav ? "Remover dos favoritos" : "Salvar"}
                                  className="rounded-full p-1 transition">
                                  <Heart className={`h-5 w-5 ${isFav ? "fill-primary text-primary" : "text-muted-foreground/30 hover:text-muted-foreground"}`} />
                                </button>
                                <a href={goUrl(l.id, "web")}
                                  target="_blank" rel="nofollow noopener noreferrer"
                                  onClick={() => posthog.capture("listing_external_clicked", { listing_id: l.id, context: "search_table" })}
                                  className="rounded-full p-1 text-muted-foreground/30 transition hover:text-muted-foreground">
                                  <ExternalLink className="h-5 w-5" />
                                </a>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {query.hasNextPage && (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                  className="chip rounded-full px-6 py-3 text-sm font-medium text-foreground transition dark:hover:bg-white/10 hover:bg-black/10 disabled:cursor-wait disabled:opacity-50"
                >
                  {query.isFetchingNextPage
                    ? "Carregando mais…"
                    : `Carregar mais (${total - listings.length} restantes)`}
                </button>
              </div>
            )}
        </div>
      </section>

      {/* Comparador FAB */}
      {comparadorItems.length >= 2 && !comparadorOpen && (
        <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
          <div className="btn-primary inline-flex items-stretch overflow-hidden rounded-full shadow-lg">
            <button type="button"
              onClick={() => setComparadorOpen(true)}
              className="inline-flex items-center gap-2 whitespace-nowrap px-5 py-2.5 text-sm font-medium"
            >
              <BarChart2 className="h-4 w-4" />
              Comparar {comparadorItems.length}
            </button>
            <div className="w-px self-stretch bg-white/25 my-1.5" />
            <button type="button"
              onClick={() => setComparadorItems([])}
              aria-label="Limpar seleção do comparador"
              className="inline-flex items-center px-3 py-2.5 transition hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {selectedListing && (
        <ListingDetailDrawer
          listing={selectedListing}
          session={session}
          isInComparador={comparadorItems.some((c) => c.id === selectedListing.id)}
          onClose={() => setSelectedListing(null)}
          onToggleComparador={toggleComparador}
          onOpenSimilar={(l) => setSelectedListing(l)}
          onRequestLogin={() => setLoginGateOpen(true)}
        />
      )}

      {loginGateOpen && <LoginGateModal onClose={() => setLoginGateOpen(false)} />}

      {comparadorOpen && (
        <ComparadorPanel
          listings={comparadorItems}
          session={session}
          onRemove={(id) => {
            setComparadorItems((prev) => prev.filter((l) => l.id !== id));
            if (comparadorItems.length <= 2) setComparadorOpen(false);
          }}
          onClose={() => setComparadorOpen(false)}
        />
      )}
    </>
  );
}

// ─── Sortable table header ─────────────────────────────────────────────────

function SortableHeader({
  col, current, dir, onClick, children, className = "",
}: {
  col: SearchSort; current: SearchSort; dir: SearchDir;
  onClick: (col: SearchSort) => void;
  children: React.ReactNode; className?: string;
}) {
  const active = current === col;
  return (
    <button
      type="button"
      onClick={() => onClick(col)}
      className={`group flex w-full items-center gap-1 transition hover:text-foreground ${active ? "text-foreground" : ""} ${className}`}
    >
      <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center">
        {active
          ? (dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)
          : <ChevronsUpDown className="h-3 w-3 opacity-30 group-hover:opacity-60" />}
      </span>
      <span>{children}</span>
    </button>
  );
}

// ─── Filter panel (sidebar + sheet content) ────────────────────────────────

function FilterPanelBody({
  search,
  selectedModels,
  onAddModel,
  onRemoveModel,
  onApply,
  onCreateAlert,
  showModelSearch = true,
}: {
  search: SearchState;
  selectedModels?: string[];
  modelLabels?: Record<string, string>;
  onAddModel?: (slug: string, label?: string) => void;
  onRemoveModel?: (slug: string) => void;
  onApply: (p: Partial<SearchState>) => void;
  onCreateAlert?: (p: Partial<SearchState>) => void;
  showModelSearch?: boolean;
}) {
  const [minYear, setMinYear] = useState(search.min_year?.toString() ?? "");
  const [maxYear, setMaxYear] = useState(search.max_year?.toString() ?? "");
  const [minKm, setMinKm] = useState(search.min_km?.toString() ?? "");
  const [maxKm, setMaxKm] = useState(search.max_km?.toString() ?? "");
  const [minPrice, setMinPrice] = useState(search.min_price?.toString() ?? "");
  const [maxPrice, setMaxPrice] = useState(search.max_price?.toString() ?? "");
  const [location, setLocation] = useState(search.location ?? "all");

  useEffect(() => {
    setMinYear(search.min_year?.toString() ?? "");
    setMaxYear(search.max_year?.toString() ?? "");
    setMinKm(search.min_km?.toString() ?? "");
    setMaxKm(search.max_km?.toString() ?? "");
    setMinPrice(search.min_price?.toString() ?? "");
    setMaxPrice(search.max_price?.toString() ?? "");
    setLocation(search.location ?? "all");
  }, [search.min_year, search.max_year, search.min_km, search.max_km, search.min_price, search.max_price, search.location]);

  const computePatch = (): Partial<SearchState> => ({
    min_year: minYear ? parseInt(minYear, 10) : undefined,
    max_year: maxYear ? parseInt(maxYear, 10) : undefined,
    min_km: minKm ? parseInt(minKm, 10) : undefined,
    max_km: maxKm ? parseInt(maxKm, 10) : undefined,
    min_price: minPrice ? parseInt(minPrice, 10) : undefined,
    max_price: maxPrice ? parseInt(maxPrice, 10) : undefined,
    location: location === "all" ? undefined : location,
  });

  const apply = () => onApply(computePatch());
  const createAlert = () => { const p = computePatch(); onApply(p); onCreateAlert?.(p); };

  const inputBase = "w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none transition focus:border-primary/60 dark:focus:bg-white/[0.05] focus:bg-black/[0.05]";

  return (
    <div className="space-y-5">
      {/* Model multi-select — desktop sidebar only */}
      {showModelSearch && (
        <>
          <div>
            <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Modelo
            </label>
            <ModelSearchBar
              onPick={(patch) => { const slug = patch.family ?? patch.model; if (slug) onAddModel?.(slug, patch.label); }}
              onClear={() => {}}
            />
            {(selectedModels ?? []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(selectedModels ?? []).map((slug) => (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => onRemoveModel?.(slug)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs text-foreground transition hover:bg-primary/20"
                  >
                    {slugToLabel(slug)}
                    <X className="h-3 w-3 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="border-t dark:border-white/[0.06] border-black/[0.06]" />
        </>
      )}

      {/* Numeric filters */}
      <div>
        <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Localização
        </label>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className={inputBase} aria-label="Localização">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo o Brasil</SelectItem>
            {LOCATIONS.map((l) => (
              <SelectItem key={l.slug} value={l.slug}>{l.display_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Ano mín.">
          <input
            inputMode="numeric" maxLength={4}
            value={minYear}
            onChange={(e) => setMinYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="2022" className={inputBase}
          />
        </Field>
        <Field label="Ano máx.">
          <input
            inputMode="numeric" maxLength={4}
            value={maxYear}
            onChange={(e) => setMaxYear(e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="2025" className={inputBase}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="KM mín.">
          <input
            inputMode="numeric"
            value={minKm ? formatBR(minKm) : ""}
            onChange={(e) => setMinKm(e.target.value.replace(/\D/g, ""))}
            placeholder="1.000" className={inputBase}
          />
        </Field>
        <Field label="KM máx.">
          <input
            inputMode="numeric"
            value={maxKm ? formatBR(maxKm) : ""}
            onChange={(e) => setMaxKm(e.target.value.replace(/\D/g, ""))}
            placeholder="50.000" className={inputBase}
          />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Preço mín.">
          <input
            inputMode="numeric"
            value={minPrice ? `R$ ${formatBR(minPrice)}` : ""}
            onChange={(e) => setMinPrice(e.target.value.replace(/\D/g, ""))}
            placeholder="150.000" className={inputBase}
          />
        </Field>
        <Field label="Preço máx.">
          <input
            inputMode="numeric"
            value={maxPrice ? `R$ ${formatBR(maxPrice)}` : ""}
            onChange={(e) => setMaxPrice(e.target.value.replace(/\D/g, ""))}
            placeholder="350.000" className={inputBase}
          />
        </Field>
      </div>

      <button
        type="button"
        onClick={apply}
        className="btn-primary w-full rounded-full px-5 py-3 text-sm font-medium"
      >
        Aplicar filtros
      </button>

      {onCreateAlert && (
        <button
          type="button"
          onClick={createAlert}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-full border dark:border-white/10 border-black/10 px-5 py-3 text-sm font-medium text-muted-foreground transition dark:hover:border-white/20 hover:border-black/20 hover:text-foreground"
        >
          <Bell className="h-3.5 w-3.5" />
          Criar alerta
        </button>
      )}
    </div>
  );
}

// ─── Mobile sheet ──────────────────────────────────────────────────────────

function FiltersSheet({
  search, onChange, onCreateAlert, className = "",
}: {
  search: SearchState;
  onChange: (p: Partial<SearchState>) => void;
  onCreateAlert?: (p: Partial<SearchState>) => void;
  className?: string;
}) {
  const activeCount =
    [search.min_year, search.max_year, search.min_km, search.max_km, search.min_price, search.max_price, search.location].filter(Boolean).length;

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          className={`chip inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-foreground transition dark:hover:bg-white/10 hover:bg-black/10 ${className}`}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filtros
          {activeCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/30 px-1.5 text-[11px] font-semibold text-foreground">
              {activeCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Filtros</SheetTitle>
          <SheetDescription>Modelos, ano, KM, preço e localização.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 px-4 pb-8">
          <FilterPanelBody
            search={search}
            showModelSearch={false}
            onApply={onChange}
            onCreateAlert={onCreateAlert}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Sort control ──────────────────────────────────────────────────────────

function SortControl({
  sort, dir, onChange,
}: {
  sort: SearchSort; dir: SearchDir;
  onChange: (sort: SearchSort, dir: SearchDir) => void;
}) {
  const key = `${sort}:${dir}`;
  const options = [
    { value: "deal_score:desc", label: "Melhores negócios", sort: "deal_score" as SearchSort, dir: "desc" as SearchDir },
    { value: "price:asc",  label: "Menor preço",  sort: "price" as SearchSort, dir: "asc"  as SearchDir },
    { value: "price:desc", label: "Maior preço",  sort: "price" as SearchSort, dir: "desc" as SearchDir },
    { value: "year:desc",  label: "Mais novo",    sort: "year"  as SearchSort, dir: "desc" as SearchDir },
    { value: "year:asc",   label: "Mais antigo",  sort: "year"  as SearchSort, dir: "asc"  as SearchDir },
    { value: "km:asc",     label: "Menor KM",     sort: "km"    as SearchSort, dir: "asc"  as SearchDir },
  ];
  return (
    <Select value={key} onValueChange={(v) => { const o = options.find((x) => x.value === v); if (o) onChange(o.sort, o.dir); }}>
      <SelectTrigger className="chip h-auto w-auto rounded-full border-0 px-4 py-2 text-sm text-foreground" aria-label="Ordenar">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// ─── Inline filter pills (desktop) ────────────────────────────────────────

const pillBase = "inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium transition cursor-pointer whitespace-nowrap";
const pillIdle = "chip text-foreground/80 hover:text-foreground";
const pillActive = "border border-primary/40 bg-primary/10 text-foreground";
const popoverInput = "w-full rounded-lg border dark:border-white/10 border-black/10 dark:bg-white/[0.03] bg-black/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary/60";

function LocationPill({ search, onUpdate, className = "" }: { search: SearchState; onUpdate: (p: Partial<SearchState>) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const current = LOCATIONS.find((l) => l.slug === search.location);
  const isSet = !!current;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${pillBase} ${isSet ? pillActive : pillIdle} ${className}`}>
          {isSet ? current.display_name.replace(/ \([A-Z]+\)$/, "") : "Estado"}
          {isSet
            ? <X className="h-3.5 w-3.5 text-muted-foreground" onClick={(e) => { e.stopPropagation(); onUpdate({ location: undefined }); setOpen(false); }} />
            : <ChevronDown className="h-3.5 w-3.5 opacity-50" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-1" align="start">
        {[{ slug: "", display_name: "Todo o Brasil" }, ...LOCATIONS].map((l) => {
          const active = (l.slug === "" && !search.location) || l.slug === search.location;
          return (
            <button key={l.slug || "all"} type="button"
              onClick={() => { posthog.capture("search_filter_applied", { filter: "location", value: l.slug || null }); onUpdate({ location: l.slug || undefined }); setOpen(false); }}
              className={`w-full rounded-md px-3 py-2 text-left text-sm transition ${active ? "bg-primary/10 font-medium text-foreground" : "text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"}`}
            >
              {l.display_name}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

function YearPill({ search, onUpdate, className = "" }: { search: SearchState; onUpdate: (p: Partial<SearchState>) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const [minYear, setMinYear] = useState(search.min_year?.toString() ?? "");
  const [maxYear, setMaxYear] = useState(search.max_year?.toString() ?? "");
  const isSet = !!(search.min_year || search.max_year);
  useEffect(() => { setMinYear(search.min_year?.toString() ?? ""); setMaxYear(search.max_year?.toString() ?? ""); }, [search.min_year, search.max_year]);
  const apply = () => { posthog.capture("search_filter_applied", { filter: "year", min_year: minYear || null, max_year: maxYear || null }); onUpdate({ min_year: minYear ? parseInt(minYear, 10) : undefined, max_year: maxYear ? parseInt(maxYear, 10) : undefined }); setOpen(false); };
  const clear = (e: React.MouseEvent) => { e.stopPropagation(); setMinYear(""); setMaxYear(""); onUpdate({ min_year: undefined, max_year: undefined }); };
  const label = isSet ? `${search.min_year ?? "?"}–${search.max_year ?? "?"}` : "Ano";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${pillBase} ${isSet ? pillActive : pillIdle} ${className}`}>
          {label}
          {isSet ? <X className="h-3.5 w-3.5 text-muted-foreground" onClick={clear} /> : <ChevronDown className="h-3.5 w-3.5 opacity-50" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3" align="start">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Mín.</span>
            <input inputMode="numeric" maxLength={4} value={minYear} onChange={(e) => setMinYear(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="2020" className={popoverInput} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Máx.</span>
            <input inputMode="numeric" maxLength={4} value={maxYear} onChange={(e) => setMaxYear(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="2025" className={popoverInput} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </label>
        </div>
        <button type="button" onClick={apply} className="btn-primary mt-3 w-full rounded-full py-2 text-xs font-medium">Aplicar</button>
      </PopoverContent>
    </Popover>
  );
}

function KmPill({ search, onUpdate, className = "" }: { search: SearchState; onUpdate: (p: Partial<SearchState>) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const [minKm, setMinKm] = useState(search.min_km?.toString() ?? "");
  const [maxKm, setMaxKm] = useState(search.max_km?.toString() ?? "");
  const isSet = !!(search.min_km || search.max_km);
  useEffect(() => { setMinKm(search.min_km?.toString() ?? ""); setMaxKm(search.max_km?.toString() ?? ""); }, [search.min_km, search.max_km]);
  const apply = () => { posthog.capture("search_filter_applied", { filter: "km", min_km: minKm || null, max_km: maxKm || null }); onUpdate({ min_km: minKm ? parseInt(minKm, 10) : undefined, max_km: maxKm ? parseInt(maxKm, 10) : undefined }); setOpen(false); };
  const clear = (e: React.MouseEvent) => { e.stopPropagation(); setMinKm(""); setMaxKm(""); onUpdate({ min_km: undefined, max_km: undefined }); };
  const label = isSet
    ? [search.min_km ? `de ${fmtBR(search.min_km)} km` : "", search.max_km ? `até ${fmtBR(search.max_km)} km` : ""].filter(Boolean).join(" ")
    : "KM";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${pillBase} ${isSet ? pillActive : pillIdle} ${className}`}>
          {label}
          {isSet ? <X className="h-3.5 w-3.5 text-muted-foreground" onClick={clear} /> : <ChevronDown className="h-3.5 w-3.5 opacity-50" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3" align="start">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">KM mín.</span>
            <input inputMode="numeric" value={minKm ? formatBR(minKm) : ""} onChange={(e) => setMinKm(e.target.value.replace(/\D/g, ""))} placeholder="1.000" className={popoverInput} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">KM máx.</span>
            <input inputMode="numeric" value={maxKm ? formatBR(maxKm) : ""} onChange={(e) => setMaxKm(e.target.value.replace(/\D/g, ""))} placeholder="80.000" className={popoverInput} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </label>
        </div>
        <button type="button" onClick={apply} className="btn-primary mt-3 w-full rounded-full py-2 text-xs font-medium">Aplicar</button>
      </PopoverContent>
    </Popover>
  );
}

function PricePill({ search, onUpdate, className = "" }: { search: SearchState; onUpdate: (p: Partial<SearchState>) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(search.min_price?.toString() ?? "");
  const [maxPrice, setMaxPrice] = useState(search.max_price?.toString() ?? "");
  const isSet = !!(search.min_price || search.max_price);
  useEffect(() => { setMinPrice(search.min_price?.toString() ?? ""); setMaxPrice(search.max_price?.toString() ?? ""); }, [search.min_price, search.max_price]);
  const apply = () => { posthog.capture("search_filter_applied", { filter: "price", min_price: minPrice || null, max_price: maxPrice || null }); onUpdate({ min_price: minPrice ? parseInt(minPrice, 10) : undefined, max_price: maxPrice ? parseInt(maxPrice, 10) : undefined }); setOpen(false); };
  const clear = (e: React.MouseEvent) => { e.stopPropagation(); setMinPrice(""); setMaxPrice(""); onUpdate({ min_price: undefined, max_price: undefined }); };
  const label = isSet ? `${search.min_price ? fmtPrice(search.min_price) : "?"} – ${search.max_price ? fmtPrice(search.max_price) : "?"}` : "Preço";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={`${pillBase} ${isSet ? pillActive : pillIdle} ${className}`}>
          {label}
          {isSet ? <X className="h-3.5 w-3.5 text-muted-foreground" onClick={clear} /> : <ChevronDown className="h-3.5 w-3.5 opacity-50" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="start">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Mín.</span>
            <input inputMode="numeric" value={minPrice ? `R$ ${formatBR(minPrice)}` : ""} onChange={(e) => setMinPrice(e.target.value.replace(/\D/g, ""))} placeholder="R$ 150.000" className={popoverInput} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Máx.</span>
            <input inputMode="numeric" value={maxPrice ? `R$ ${formatBR(maxPrice)}` : ""} onChange={(e) => setMaxPrice(e.target.value.replace(/\D/g, ""))} placeholder="R$ 350.000" className={popoverInput} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </label>
        </div>
        <button type="button" onClick={apply} className="btn-primary mt-3 w-full rounded-full py-2 text-xs font-medium">Aplicar</button>
      </PopoverContent>
    </Popover>
  );
}

// ─── New-user hints banner ─────────────────────────────────────────────────

function NewUserHints() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem("bb_hints_seen")) setVisible(true);
    } catch {
      // localStorage unavailable (private mode edge case)
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem("bb_hints_seen", "1"); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="surface-card mb-4 flex items-start justify-between gap-3 rounded-2xl border border-primary/20 px-5 py-3.5 text-sm">
      <p className="text-muted-foreground">
        <span className="font-medium text-foreground">Dicas:</span>{" "}
        ordene por ano, KM ou preço clicando nos cabeçalhos da tabela
        {" "}·{" "}
        <Heart className="mb-0.5 inline h-3.5 w-3.5 text-primary" /> salva nos favoritos
        {" "}·{" "}
        <PlusCircle className="mb-0.5 inline h-3.5 w-3.5 text-primary" /> adiciona ao comparador
      </p>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Fechar dicas"
        className="shrink-0 text-muted-foreground/60 transition hover:text-muted-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ─── Utility ──────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function formatBR(n: string) {
  const digits = n.replace(/\D/g, "");
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}
